import {
  faChevronDown,
  faCircleQuestion,
} from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  HeadlineL,
  HeadlineXS,
} from '@sorare/core/src/atoms/typography';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { faqMessages } from './faqMessages';

const FaqItemRoot = styled.div`
  width: 100%;
  padding: var(--intermediate-unit);
  background-color: var(--c-nd-50);
  border-radius: var(--unit);
  text-align: left;
`;

const FaqItemHeader = styled(SBHorizontal)`
  width: 100%;
  text-align: left;
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)<{ open: boolean }>`
  ${({ open }) => open && 'transform: rotate(180deg);'}
`;

const FaqItem = ({
  question,
  answer,
}: {
  question: MessageDescriptor;
  answer: MessageDescriptor;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqItemRoot>
      <FaqItemHeader gap={2} as={ButtonBase} onClick={() => setOpen(!open)}>
        <HeadlineXS as="span">
          <FormattedMessage {...question} />
        </HeadlineXS>
        <StyledFontAwesomeIcon open={open} icon={faChevronDown} />
      </FaqItemHeader>
      <Collapse open={open}>
        <BodyM color="var(--c-nd-600-opaque)">
          <FormattedMessage {...answer} />
        </BodyM>
      </Collapse>
    </FaqItemRoot>
  );
};

const StyledDrawer = styled(Drawer)`
  ${regularMaterial}
  width: 100%;
  padding: var(--triple-unit) var(--double-unit);
  @media ${laptopAndAbove} {
    width: 440px;
  }
`;
type Props = {
  open: boolean;
  onClose: () => void;
};

export const FAQDrawer = ({ open, onClose }: Props) => {
  const isLaptop = useIsLaptop();

  return (
    <StyledDrawer
      side={isLaptop ? 'right' : 'bottom'}
      open={open}
      onBackdropClick={onClose}
    >
      <Vertical center gap={3}>
        <FontAwesomeIcon size="lg" icon={faCircleQuestion} />
        <HeadlineL>
          <FormattedMessage {...glossary.faq} />
        </HeadlineL>
        <Vertical>
          {faqMessages.map((faq, index) => (
            <FaqItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              question={faq.question}
              answer={faq.answer}
            />
          ))}
        </Vertical>
      </Vertical>
    </StyledDrawer>
  );
};
